import { motion } from "framer-motion";
import { Emblem } from "../brand/Logo";
import { Eyebrow } from "../common/Primitives";
import { Reveal, Stagger, StaggerItem } from "../motion/Reveal";

const POINTS = [
  { k: "01", t: "Bottles tracked", d: "Deliveries and returns are logged against each partner property." },
  { k: "02", t: "Plastic avoided", d: "An estimate of single-use plastic bottles your returnable service replaced." },
  { k: "03", t: "Carbon estimate", d: "Estimated emissions saved, calculated with a documented, disclosed methodology." },
];

const Field = ({ label, value }) => (
  <div className="border-t border-[#B18A4A]/30 pt-3">
    <p className="text-[9px] font-bold uppercase tracking-[0.26em] text-[#8C6A3C]">{label}</p>
    <p className="font-serif-lux mt-1 text-2xl text-[#26231F] sm:text-3xl">{value}</p>
  </div>
);

export const ImpactCertificate = () => (
  <div className="relative overflow-hidden bg-[#21170F] grain" data-testid="impact-certificate-section">
    <div className="mx-auto grid max-w-[1440px] items-center gap-16 px-5 py-24 sm:px-8 lg:grid-cols-[1fr_1fr] lg:px-12 lg:py-32">
      <div>
        <Reveal>
          <Eyebrow>Quarterly Impact Certificate</Eyebrow>
          <h2 className="h-section mt-6 text-[#F8F3E9]"><span className="block">Proof, Delivered</span><span className="block italic text-[#E8D6B7]">Every Quarter</span></h2>
          <p className="mt-8 max-w-md text-[15px] leading-[1.8] text-[#F8F3E9]/75">
            Every TOYAM partner will receive a quarterly certificate summarising the single-use plastic their returnable service helped
            eliminate and the estimated carbon emissions saved — a credible, shareable record for guests, owners and ESG reporting.
          </p>
        </Reveal>
        <Stagger className="mt-12 space-y-7">
          {POINTS.map((p) => (
            <StaggerItem key={p.k} className="flex gap-6">
              <span className="font-serif-lux text-xl text-[#B18A4A]">{p.k}</span>
              <div>
                <h3 className="font-sans text-[11px] font-bold uppercase tracking-[0.24em] text-[#F8F3E9]">{p.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[#F8F3E9]/70">{p.d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <p className="mt-10 max-w-md text-xs leading-relaxed text-[#E8D6B7]/70">
          Figures are estimates based on actual bottles returned and refilled for each partner; actual impact depends on return rates,
          collection distances, washing and logistics. The calculation methodology will be shared with every certificate.
        </p>
      </div>

      <motion.div className="relative mx-auto w-full max-w-[520px]" style={{ perspective: 1200 }}
        initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}>
        <motion.div whileHover={{ rotateX: 4, rotateY: -6 }} transition={{ type: "spring", stiffness: 120, damping: 18 }}
          className="relative bg-[#F8F3E9] p-3 shadow-[0_50px_100px_-30px_rgba(0,0,0,.7)]" data-testid="impact-certificate-card">
          <div className="border border-[#B18A4A]/60 p-7 sm:p-10">
            <div className="flex items-center justify-between">
              <Emblem className="h-5 w-auto text-[#B18A4A]" />
              <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-[#8C6A3C]">Q_ · 20__</span>
            </div>
            <p className="mt-8 text-center text-[10px] font-bold uppercase tracking-[0.34em] text-[#8C6A3C]">Certificate of Impact</p>
            <p className="font-serif-lux mt-3 text-center text-3xl text-[#26231F] sm:text-4xl">[Partner Property Name]</p>
            <p className="mt-3 text-center text-[13px] text-[#746D64]">in recognition of its returnable glass water service with TOYAM</p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <Field label="Single-use plastic bottles avoided" value="—,———" />
              <Field label="Estimated CO₂e saved" value="— kg" />
              <Field label="Glass bottles returned" value="—,———" />
              <Field label="Return rate" value="— %" />
            </div>
            <div className="mt-10 flex items-end justify-between">
              <span className="font-serif-lux text-lg italic text-[#26231F]">TOYAM</span>
              <span className="text-[9px] uppercase tracking-[0.24em] text-[#746D64]">Illustrative sample</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </div>
);
